import React, { useState, useRef } from 'react'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Check, X } from 'lucide-react'

export default function ResizableCropModal({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  title = "Crop Image"
}) {
  const imgRef = useRef(null)
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const [loading, setLoading] = useState(false)

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return null

    const image = imgRef.current
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = completedCrop.width * scaleX
    canvas.height = completedCrop.height * scaleY

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    )

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' })
        resolve({ file, url: URL.createObjectURL(blob) })
      }, 'image/jpeg', 0.95)
    })
  }

  const handleConfirm = async () => {
    try {
      setLoading(true)
      const croppedImage = await getCroppedImg()
      if (croppedImage) {
        // Validate cropped image size (100KB min)
        const minSize = 100 * 1024 // 100KB in bytes
        if (croppedImage.file.size < minSize) {
          alert('Cropped image is too small (minimum 100KB required). Please crop a larger area or use a higher resolution image.')
          setLoading(false)
          return
        }

        onCropComplete(croppedImage)
        handleClose()
      }
    } catch (e) {
      console.error('Error cropping image:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setCrop(undefined)
    setCompletedCrop(undefined)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Cropper Container */}
          <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ maxHeight: '60vh' }}>
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={setCrop}
                onComplete={setCompletedCrop}
                className="max-h-full"
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop"
                  style={{ maxHeight: '60vh', width: 'auto', margin: '0 auto', display: 'block' }}
                  onLoad={(e) => {
                    const { width, height } = e.currentTarget
                    // Set initial crop to center 50% of the image
                    const initialCrop = {
                      unit: '%',
                      x: 25,
                      y: 25,
                      width: 50,
                      height: 50
                    }
                    setCrop(initialCrop)
                  }}
                />
              </ReactCrop>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              ✓ Click and drag to select the area you want to keep. Drag corners to resize.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading || !completedCrop}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Crop
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}